/**
 * BLRRD paint engine — Smudge (pull-smudge accumulator) (Phase 4.C.paint.2,
 * Wave 9.2).
 *
 * Drags existing canvas colour along the stroke direction. The
 * accumulator carries colour forward from dab to dab (dossier §2.5):
 *
 *   On stroke start:  accum = read brush region at the start position.
 *   On each dab:      sampled = read brush region at (cx, cy)
 *                     blended = sampled·(1−rate) + accum·rate
 *                     write blended through the brush mask into the layer
 *                     accum = blended            (carry colour forward)
 *
 * `rate` ∈ [0, 1] is how much "old" colour carries forward (Photoshop's
 * "Strength", GIMP's "Rate"). Default 0.5. Lower = colours drag less.
 *
 * The accumulator is a small `maskSize² × 4` Float32 buffer keyed to the
 * cached brush mask size. It MUST be reset on every new stroke — leftover
 * from a previous stroke would create a ghost smudge at stroke start
 * (dossier §2.5 numerical-caution note). The caller owns the accumulator
 * (held on the stroke state) and passes it via `brush.smudge`.
 *
 * Edge behaviour: when the brush region partly falls off the layer, the
 * sampler clamps (replicates edge pixels) — reflecting or wrapping would
 * create artifacts (dossier §2.5).
 *
 * Provenance (clean-room): classical "drag colours along stroke" graphics
 * (Hughes/van Dam §10.5); the accumulator formulation predates Photoshop
 * and is documented in mid-1980s graphics texts. NO GIMP/GEGL source.
 *
 * @module src/pipeline/paint/smudge
 */

import { dabBounds, boundsToRegion, worldToMaskCell, sampleMaskBilinear, ALPHA_EPS } from './brush-ops.js';
import { bilinearSampleRGBA } from './clone.js';

/** Default smudge rate. */
export const DEFAULT_SMUDGE_RATE = 0.5;

/**
 * Create a fresh smudge accumulator sized to the brush mask. Call once at
 * stroke start, then prime it with `primeSmudge`.
 *
 * @param {number} maskSize
 * @returns {{ maskSize: number, accum: Float32Array, primed: boolean }}
 */
export function createSmudgeAccum(maskSize) {
    const D = maskSize | 0;
    return { maskSize: D, accum: new Float32Array(D * D * 4), primed: false };
}

/**
 * Prime the accumulator from the layer's brush region at (cx, cy). The
 * accumulator is sampled on the same world→cell grid the dab uses, so
 * subsequent blends line up. Sets `primed = true`.
 *
 * @param {object} sm        from createSmudgeAccum
 * @param {Float32Array} rgba
 * @param {number} w
 * @param {number} h
 * @param {number} cx
 * @param {number} cy
 * @param {number} effSize
 */
export function primeSmudge(sm, rgba, w, h, cx, cy, effSize) {
    const D = sm.maskSize;
    const r = effSize / 2;
    const ox = cx - r, oy = cy - r;
    const acc = sm.accum;
    for (let my = 0; my < D; my++) {
        /* Cell-centre → world coordinate (inverse of worldToMaskCell). */
        const wy = oy + ((my + 0.5) / D) * effSize;
        for (let mx = 0; mx < D; mx++) {
            const wx = ox + ((mx + 0.5) / D) * effSize;
            const s = bilinearSampleRGBA(rgba, w, h, wx - 0.5, wy - 0.5);
            const k = (my * D + mx) * 4;
            acc[k] = s[0]; acc[k + 1] = s[1]; acc[k + 2] = s[2]; acc[k + 3] = s[3];
        }
    }
    sm.primed = true;
}

/**
 * Smudge dab — pull-smudge accumulator step. Reads the current brush
 * region, blends with the accumulator, writes through the mask, and
 * carries the blended colour forward.
 *
 * @param {Float32Array} rgba   active-layer buffer (mutated in place)
 * @param {number} w
 * @param {number} h
 * @param {number} cx
 * @param {number} cy
 * @param {object} brush
 * @param {Float32Array} brush.mask
 * @param {number} brush.maskSize
 * @param {number} brush.effSize
 * @param {number} brush.flow
 * @param {number} brush.rate          [0, 1] colour-carry strength
 * @param {object} brush.smudge        accumulator from createSmudgeAccum
 * @returns {{x:number,y:number,w:number,h:number}|null} dirty region
 */
export function stampSmudgeDab(rgba, w, h, cx, cy, brush) {
    const { mask, maskSize, effSize, flow, smudge } = brush;
    if (!smudge || !smudge.accum) return null;
    const b = dabBounds(cx, cy, effSize, w, h);
    if (!b) return null;
    const rate = clamp01(brush.rate == null ? DEFAULT_SMUDGE_RATE : brush.rate);
    const f = flow == null ? 1 : clamp01(flow);
    if (f <= 0) return null;
    const r = effSize / 2;
    const ox = cx - r, oy = cy - r;
    const D = smudge.maskSize;
    const acc = smudge.accum;

    /* If the brush size changed mid-stroke and the accumulator no longer
     * matches the mask, fall back to priming from the current region so we
     * never index out of bounds. */
    if (D !== maskSize) {
        return null;       /* size change during smudge — caller re-primes */
    }

    for (let y = b.y0; y <= b.y1; y++) {
        const my = worldToMaskCell(y + 0.5, oy, effSize, maskSize);
        const myi = clampIdx(Math.round(my), maskSize);
        const row = y * w * 4;
        for (let x = b.x0; x <= b.x1; x++) {
            const mx = worldToMaskCell(x + 0.5, ox, effSize, maskSize);
            const cov = sampleMaskBilinear(mask, maskSize, mx, my);
            if (cov <= 0) continue;
            const as = cov * f;
            if (as <= 0) continue;
            const mxi = clampIdx(Math.round(mx), maskSize);
            const k = (myi * D + mxi) * 4;
            const j = row + x * 4;
            /* Current layer pixel (straight alpha). */
            const dr = rgba[j], dg = rgba[j + 1], db = rgba[j + 2], ad = rgba[j + 3];
            /* Blend toward the accumulator (the carried colour). */
            const br = acc[k] * rate + dr * (1 - rate);
            const bg = acc[k + 1] * rate + dg * (1 - rate);
            const bb = acc[k + 2] * rate + db * (1 - rate);
            const ba = acc[k + 3] * rate + ad * (1 - rate);
            /* Write the blended colour through the mask coverage (over the
             * existing pixel). Smudge preserves the layer's own alpha
             * profile — it moves colour, it doesn't add coverage. */
            rgba[j]     = dr + (br - dr) * as;
            rgba[j + 1] = dg + (bg - dg) * as;
            rgba[j + 2] = db + (bb - db) * as;
            rgba[j + 3] = ad + (ba - ad) * as;
            /* Carry the blended colour forward into the accumulator. */
            acc[k] = br; acc[k + 1] = bg; acc[k + 2] = bb; acc[k + 3] = ba;
        }
    }
    return boundsToRegion(b);
}

function clamp01(v) {
    if (!Number.isFinite(v)) return 0;
    return v < 0 ? 0 : v > 1 ? 1 : v;
}
function clampIdx(i, n) {
    if (i < 0) return 0;
    if (i > n - 1) return n - 1;
    return i;
}

/* ALPHA_EPS imported for parity with the other ops though smudge doesn't
 * un-premultiply (it moves straight-alpha colour in place). */
export { ALPHA_EPS };
